import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { ConfirmedIcon as Check } from '@/components/icons/line/confirmed';
import { BackIcon as ChevronLeft } from '@/components/icons/line/back';
import { ForwardIcon as ChevronRight } from '@/components/icons/line/forward';
import { CopyIcon as Copy } from '@/components/icons/line/copy';
import { MenuIcon } from '@/components/icons/line/menu';
import { MobileIcon as Smartphone } from '@/components/icons/line/mobile';
import { CloseIcon as X } from '@/components/icons/line/close';
import { useLocation } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import {
  detectInstallPlatform,
  isMobileInstallPlatform,
  type InstallPlatform,
} from '@/lib/install-platform';

/**
 * The trip planner's composer owns the bottom of the phone screen. When that
 * anchor is mounted, the banner docks above the type field instead of covering it.
 */
let installBannerSlot: HTMLElement | null = null;
const installBannerSlotListeners = new Set<() => void>();

function notifyInstallBannerSlot() {
  for (const listener of installBannerSlotListeners) listener();
}

export function registerInstallBannerSlot(node: HTMLElement | null) {
  installBannerSlot = node;
  notifyInstallBannerSlot();
}

function subscribeInstallBannerSlot(listener: () => void) {
  installBannerSlotListeners.add(listener);
  return () => installBannerSlotListeners.delete(listener);
}

const BANNER_DISMISSED_KEY = 'roam:pwa-install-dismissed-at';
const BANNER_DISMISS_MS = 1000 * 60 * 60 * 24 * 30;
const INSTALL_INTENT_KEY = 'roam:install-intent';

/**
 * Chrome's install event. Not in lib.dom — the API never left the incubator
 * spec — so typed here.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * `beforeinstallprompt` can fire before React mounts, so the listener is
 * registered at module import (main.tsx imports the route tree eagerly), not
 * in an effect. Tiny store: latest event + change subscribers.
 */
let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
let appInstalled = false;
const installPromptListeners = new Set<() => void>();

function notifyInstallPromptListeners() {
  for (const listener of installPromptListeners) listener();
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredInstallPrompt = event as BeforeInstallPromptEvent;
    notifyInstallPromptListeners();
  });
  window.addEventListener('appinstalled', () => {
    deferredInstallPrompt = null;
    appInstalled = true;
    notifyInstallPromptListeners();
  });
}

const IOS_STEPS: {
  src: string;
  caption: string;
  captionVisual?: ReactNode;
  note?: string;
}[] = [
  {
    src: '/beta/step1.jpg',
    caption: 'Tap the menu button next to the address',
    captionVisual: (
      <>
        Tap the <MenuIcon size={16} className="inline align-[-0.2em] text-ink" aria-hidden /> menu
        next to the address
      </>
    ),
    note: 'On some iPhones the Share button is already visible at the bottom of Safari — if you see it, skip straight to step 2.',
  },
  { src: '/beta/step2.jpg', caption: 'Tap Share' },
  { src: '/beta/step3.jpg', caption: 'Tap View More' },
  { src: '/beta/step4.jpg', caption: 'Tap Add to Home Screen' },
  { src: '/beta/step5.jpg', caption: 'Keep "Open as Web App" on, then tap Add' },
  { src: '/beta/step6.jpg', caption: 'Open Roam from your home screen' },
];

function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return true;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone)
  );
}

function readBannerDismissed(): boolean {
  try {
    const dismissed = Number(window.localStorage.getItem(BANNER_DISMISSED_KEY) ?? '0');
    return Number.isFinite(dismissed) && Date.now() - dismissed < BANNER_DISMISS_MS;
  } catch {
    return false;
  }
}

function clearInstallIntent() {
  try {
    window.sessionStorage.removeItem(INSTALL_INTENT_KEY);
  } catch {
    // Storage unavailable (private mode edge cases) — nothing to clear.
  }
}

/**
 * "Add Roam to your Home Screen" guide for beta testers.
 *
 * Mounted once from PwaRuntime (app root), so it works on every route,
 * including marketing pages and post-auth redirect landings. Two entry
 * points: an explicit `?install=1` link from the invite email (persisted in
 * sessionStorage so it survives Clerk sign-in/sign-up redirects) and a
 * dismissible mobile banner. Renders nothing in standalone mode.
 */
export function InstallGuide({ suppressBanner = false }: { suppressBanner?: boolean }) {
  const location = useLocation();
  const [platform] = useState<InstallPlatform>(() =>
    detectInstallPlatform(typeof navigator === 'undefined' ? '' : navigator.userAgent, {
      standalone: isStandaloneDisplay(),
      maxTouchPoints: typeof navigator === 'undefined' ? 0 : navigator.maxTouchPoints,
    }),
  );
  const [guideOpen, setGuideOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(true);
  const [desktopNoteOpen, setDesktopNoteOpen] = useState(false);
  const bannerSlot = useSyncExternalStore(
    subscribeInstallBannerSlot,
    () => installBannerSlot,
    () => null,
  );

  useEffect(() => {
    setBannerDismissed(readBannerDismissed());
  }, []);

  // Persist ?install=1 intent, then act on any pending intent. Runs on every
  // route change so the guide opens once the user lands back in the app after
  // a Clerk redirect (same tab keeps sessionStorage across full page loads).
  useEffect(() => {
    let intent = false;
    try {
      if (new URLSearchParams(location.search).get('install') === '1') {
        window.sessionStorage.setItem(INSTALL_INTENT_KEY, '1');
      }
      intent = window.sessionStorage.getItem(INSTALL_INTENT_KEY) === '1';
    } catch {
      intent = new URLSearchParams(location.search).get('install') === '1';
    }
    if (!intent) return;
    if (platform === 'standalone') {
      clearInstallIntent();
      return;
    }
    // Do not interrupt Clerk's own screens; the intent survives in
    // sessionStorage and fires on the next route.
    if (/^\/(login|signup)(\/|$)/.test(location.pathname)) return;
    clearInstallIntent();
    if (platform === 'desktop') setDesktopNoteOpen(true);
    else setGuideOpen(true);
  }, [location.pathname, location.search, platform]);

  if (platform === 'standalone') return null;

  // Focused flows (Clerk screens, required setup) suppress app chrome.
  const onFocusedFlow = /^\/(login|signup|onboarding)(\/|$)/.test(location.pathname);
  const showBanner =
    isMobileInstallPlatform(platform) &&
    !bannerDismissed &&
    !guideOpen &&
    !suppressBanner &&
    !onFocusedFlow;

  const installBanner = (
    <div
      role="status"
      className={
        bannerSlot
          ? 'pwa-install-hint relative mb-3 w-full rounded-xl border border-primary/20 bg-white p-3 text-sm text-ink'
          : 'pwa-install-hint fixed bottom-[calc(var(--safe-bottom)+1rem)] left-[max(1rem,var(--safe-left))] right-[max(1rem,var(--safe-right))] z-40 mx-auto max-w-md rounded-xl border border-primary/20 bg-white/95 p-4 text-sm text-ink shadow-lg backdrop-blur'
      }
    >
      <button
        type="button"
        aria-label="Dismiss install banner"
        className="absolute right-3 top-3 rounded-full p-1 text-ink/45 transition hover:bg-ink/5 hover:text-ink"
        onClick={() => {
          try {
            window.localStorage.setItem(BANNER_DISMISSED_KEY, String(Date.now()));
          } catch {
            // Best effort; the in-memory dismissal still applies.
          }
          setBannerDismissed(true);
        }}
      >
        <X className="h-4 w-4" />
      </button>
      <button
        type="button"
        className="block w-full pr-7 text-left"
        onClick={() => setGuideOpen(true)}
      >
        <span className="flex items-center gap-2 font-bold">
          <Smartphone className="h-4 w-4 text-primary" />
          Add Roam to your Home Screen
        </span>
        <span className="mt-1 block text-xs text-ink/65">
          One tap from your home screen to your trips. See how →
        </span>
      </button>
    </div>
  );

  return (
    <>
      {showBanner ? (bannerSlot ? createPortal(installBanner, bannerSlot) : installBanner) : null}

      {desktopNoteOpen ? (
        <div
          role="status"
          className="fixed bottom-[calc(var(--safe-bottom)+1rem)] right-[max(1rem,var(--safe-right))] z-[70] max-w-xs rounded-xl border border-primary/20 bg-white/95 p-4 text-sm text-ink shadow-lg backdrop-blur"
        >
          <button
            type="button"
            aria-label="Dismiss"
            className="absolute right-3 top-3 rounded-full p-1 text-ink/45 transition hover:bg-ink/5 hover:text-ink"
            onClick={() => setDesktopNoteOpen(false)}
          >
            <X className="h-4 w-4" />
          </button>
          <p className="pr-6">
            Open <span className="font-semibold">roamtogether.ai</span> on your phone to add Roam to
            your home screen.
          </p>
        </div>
      ) : null}

      <Sheet open={guideOpen} onOpenChange={setGuideOpen}>
        <SheetContent
          side="bottom"
          className="max-h-[92dvh] overflow-y-auto rounded-t-2xl px-[max(1.25rem,var(--safe-left))] pt-5"
        >
          <div className="mx-auto w-full max-w-md pb-4">
            <SheetTitle className="pr-8 font-display">Add Roam to your Home Screen</SheetTitle>
            {platform === 'ios-safari' ? <IosSafariSteps /> : null}
            {platform === 'ios-other' ? <IosSafariHandoff /> : null}
            {platform === 'android-chrome' || platform === 'android-other' ? (
              <AndroidInstall />
            ) : null}
            {platform === 'desktop' ? (
              <SheetDescription className="mt-2">
                Open roamtogether.ai on your phone to add Roam to your home screen.
              </SheetDescription>
            ) : null}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

/** Swipeable, stepped walkthrough of the six annotated Safari screenshots. */
function IosSafariSteps() {
  const [step, setStep] = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);

  const scrollToStep = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollTo({ left: index * track.clientWidth, behavior: 'smooth' });
  }, []);

  const onTrackScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track || track.clientWidth === 0) return;
    const index = Math.round(track.scrollLeft / track.clientWidth);
    setStep(Math.min(IOS_STEPS.length - 1, Math.max(0, index)));
  }, []);

  return (
    <div>
      <SheetDescription className="mt-1">
        Six quick taps in Safari and Roam opens like an app.
      </SheetDescription>
      <div
        ref={trackRef}
        onScroll={onTrackScroll}
        className="mt-4 flex snap-x snap-mandatory overflow-x-auto scroll-smooth rounded-xl"
        aria-roledescription="carousel"
        aria-label="Add to Home Screen steps"
      >
        {IOS_STEPS.map((s, index) => (
          <figure
            key={s.src}
            className="w-full flex-shrink-0 snap-center px-0.5"
            role="group"
            aria-roledescription="slide"
            aria-label={`Step ${index + 1} of ${IOS_STEPS.length}`}
          >
            <img
              src={s.src}
              alt={s.caption}
              loading={index === 0 ? 'eager' : 'lazy'}
              width={1080}
              height={872}
              className="mx-auto max-h-[38dvh] w-auto rounded-xl border border-ink/10 object-contain"
            />
            <figcaption className="mt-3 text-center text-sm font-semibold text-ink">
              {index + 1}. {s.captionVisual ?? s.caption}
            </figcaption>
            {s.note ? <p className="mt-1 text-center text-xs text-ink/60">{s.note}</p> : null}
          </figure>
        ))}
      </div>
      <div className="mt-4 flex items-center justify-between gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={step === 0}
          onClick={() => scrollToStep(step - 1)}
        >
          <ChevronLeft aria-hidden />
          Back
        </Button>
        <div className="flex items-center gap-1.5" role="tablist" aria-label="Step">
          {IOS_STEPS.map((s, index) => (
            <button
              key={s.src}
              type="button"
              role="tab"
              aria-selected={index === step}
              aria-label={`Go to step ${index + 1}`}
              className={`h-2 rounded-full transition-all ${
                index === step ? 'w-5 bg-primary' : 'w-2 bg-ink/20 hover:bg-ink/35'
              }`}
              onClick={() => scrollToStep(index)}
            />
          ))}
        </div>
        <Button
          variant={step === IOS_STEPS.length - 1 ? 'outline' : 'default'}
          size="sm"
          disabled={step === IOS_STEPS.length - 1}
          onClick={() => scrollToStep(step + 1)}
        >
          Next
          <ChevronRight aria-hidden />
        </Button>
      </div>
    </div>
  );
}

/** In-app or non-Safari iOS browser: hand the link to Safari. */
function IosSafariHandoff() {
  const [copied, setCopied] = useState(false);
  const installUrl = useMemo(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('install', '1');
    return url.toString();
  }, []);

  return (
    <div>
      <SheetDescription className="mt-1">
        Open this page in Safari to add Roam to your home screen — this browser can&apos;t do it.
      </SheetDescription>
      <div className="mt-4 flex items-center gap-2 rounded-xl border border-ink/10 bg-ivory p-2 pl-3">
        <span className="min-w-0 flex-1 truncate text-xs text-ink/70">{installUrl}</span>
        <Button
          size="sm"
          onClick={() => {
            navigator.clipboard
              .writeText(installUrl)
              .then(() => setCopied(true))
              .catch(() => setCopied(false));
          }}
        >
          {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
          {copied ? 'Copied' : 'Copy link'}
        </Button>
      </div>
      <p className="mt-3 text-xs text-ink/60" role={copied ? 'status' : undefined}>
        {copied
          ? 'Link copied — open Safari, paste it in the address bar, and the guide will pick up from there.'
          : 'Tip: in Gmail, tap ••• in the top corner, then "Open in Safari" or "Open in browser". Or copy the link and paste it into Safari.'}
      </p>
    </div>
  );
}

/** Android: one-tap Chrome install when the event is available, else menu steps. */
function AndroidInstall() {
  const [, forceRender] = useState(0);
  useEffect(() => {
    const listener = () => forceRender((n) => n + 1);
    installPromptListeners.add(listener);
    return () => {
      installPromptListeners.delete(listener);
    };
  }, []);

  if (appInstalled) {
    return (
      <SheetDescription className="mt-1">
        Roam is installed — open it from your home screen.
      </SheetDescription>
    );
  }

  return (
    <div>
      {deferredInstallPrompt ? (
        <>
          <SheetDescription className="mt-1">
            One tap and Roam lives on your home screen.
          </SheetDescription>
          <Button
            className="mt-4 w-full"
            onClick={() => {
              const prompt = deferredInstallPrompt;
              if (!prompt) return;
              // Chrome allows one prompt() per event; drop it either way.
              deferredInstallPrompt = null;
              void prompt.prompt().catch(() => undefined);
              void prompt.userChoice.finally(notifyInstallPromptListeners);
            }}
          >
            Install Roam
          </Button>
        </>
      ) : (
        <IosSafariSteps />
      )}
    </div>
  );
}
